import React, { memo, useMemo } from "react";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import { useTranslation } from "react-i18next";

import colours from "../../theme/colours";
import { getPrimaryColour } from "../../utils/getPrimaryColour";

ChartJS.register(...registerables);

function resolveMaxSecs(datasets = []) {
  const maxFromPoints = datasets.reduce((currentMax, dataset) => {
    const datasetMax = (dataset?.points || []).reduce((pointMax, point) => {
      const value = Number(point?.t);
      return Number.isFinite(value) ? Math.max(pointMax, value) : pointMax;
    }, 0);

    return Math.max(currentMax, datasetMax);
  }, 0);

  return maxFromPoints > 0 ? Math.ceil(maxFromPoints) : 60;
}

const DEFAULT_DATASET_COLOURS = [getPrimaryColour(), colours.secondary];

const SixMinuteWalkSignalChart = memo(function SixMinuteWalkSignalChart({
  datasets = [],
  height = 220,
  maxSecs = null,
}) {
  const { t } = useTranslation();

  const chartDatasets = useMemo(
    () =>
      (Array.isArray(datasets) ? datasets : [])
        .filter((dataset) => Array.isArray(dataset?.points) && dataset.points.length)
        .map((dataset, index) => ({
          label:
            dataset.label ||
            t("result.resp.detrendedSignalLabel", "Detrended"),
          data: dataset.points.map((point) => ({
            x: point.t,
            y: point.value,
          })),
          parsing: false,
          borderColor:
            dataset.color || DEFAULT_DATASET_COLOURS[index] || colours.primary,
          backgroundColor: "transparent",
          pointRadius: 0,
          pointHoverRadius: 0,
          borderWidth: 2,
          tension: 0.08,
        })),
    [datasets, t],
  );

  const chartData = useMemo(
    () => ({
      datasets: chartDatasets,
    }),
    [chartDatasets],
  );

  const resolvedMaxSecs = useMemo(
    () => (Number.isFinite(maxSecs) ? maxSecs : resolveMaxSecs(datasets)),
    [datasets, maxSecs],
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: "nearest",
        intersect: false,
      },
      plugins: {
        legend: {
          display: chartDatasets.length > 1,
          labels: {
            color: colours.typography,
            usePointStyle: true,
            boxWidth: 10,
          },
        },
        tooltip: {
          callbacks: {
            title(items) {
              const x = items?.[0]?.parsed?.x;
              return typeof x === "number" && Number.isFinite(x)
                ? t("result.postExertion.decayTooltipTitle", {
                    defaultValue: "{{seconds}} s",
                    seconds: x.toFixed(1),
                  })
                : "";
            },
          },
        },
      },
      scales: {
        x: {
          type: "linear",
          min: 0,
          max: resolvedMaxSecs,
          title: {
            display: true,
            text: t("charts.signal.timeSeconds", "Time (s)"),
            color: colours.typography,
          },
          grid: {
            color: "rgba(147,147,147,0.2)",
          },
          ticks: {
            color: colours.typography,
          },
        },
        y: {
          grid: {
            color: "rgba(147,147,147,0.15)",
          },
          ticks: {
            display: false,
          },
          border: {
            display: false,
          },
        },
      },
    }),
    [chartDatasets.length, resolvedMaxSecs, t],
  );

  return (
    <div style={{ height }}>
      <Line data={chartData} options={options} />
    </div>
  );
});

export default SixMinuteWalkSignalChart;
